import axios from 'axios';
import { effectiveTimeZone } from '../utils/dateTime';
import { requestDatabase, urlDatabase, clearDatabaseSelection, waitForDatabaseList, rememberDatabaseList } from './activeDatabase';

// Dynamic API URL - runtime config with dev VM fallback
// On dev VMs, always use HTTP:5213 regardless of frontend protocol
export const API_BASE_URL = window.__config__?.API_BASE_URL
  || (window.location.hostname === 'localhost'
      ? 'http://localhost:5213'
      : `http://${window.location.hostname}:5213`);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Inject Bearer token for Entra auth mode
api.interceptors.request.use(async (config) => {
  const authMode = window.__config__?.AUTH_MODE || 'impersonation';
  if (authMode === 'entra') {
    try {
      const { msalInstance } = await import('../auth/authConfig');
      if (msalInstance) {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          try {
            const tokenResponse = await msalInstance.acquireTokenSilent({
              scopes: [`api://${window.__config__?.AZURE_CLIENT_ID}/access_as_user`],
              account: accounts[0],
            });
            config.headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
          } catch (silentErr) {
            console.warn('Silent token acquisition failed, redirecting:', silentErr);
            msalInstance.acquireTokenRedirect({
              scopes: [`api://${window.__config__?.AZURE_CLIENT_ID}/access_as_user`],
            });
          }
        }
      }
    } catch (err) {
      console.error('Failed to acquire auth token:', err);
    }
  }
  return config;
});

const DB_FREE_PREFIXES = ['/api/auth/', '/api/admin/', '/api/databases/'];

const blockedRequest = (config, reason) => {
  const err = new Error(`Request not sent: ${reason}. Reload the page if this persists.`);
  err.code = 'database-mismatch';
  err.config = config;
  console.error('[DAWN] blocked wrong-database request', config.method, config.url, '-', reason);
  return err;
};

// Inject this tab's database into every API request: the one its URL names on a data page, its
// own on a slug-free page (see activeDatabase.js). Never another tab's pick. A request that would
// go to a database other than the one this tab shows is refused before it leaves the browser:
// a data page whose slug does not resolve, or a caller naming a different X-Database-Id. On a
// fresh load the slug map may not know the slug yet: the database list endpoints go out bare
// (they never needed the header, and they are what fills the map), everything else waits for
// the list first.
api.interceptors.request.use(async config => {
  const isDbFree = DB_FREE_PREFIXES.some(p => (config.url || '').startsWith(p));
  let target = requestDatabase();
  if (target.blocked && !isDbFree) {
    await waitForDatabaseList();
    target = requestDatabase();
  }
  const explicit = config.headers['X-Database-Id'];
  // A development tool's preview of the source database it is pointed at: a read, from a
  // slug-free page, naming its database deliberately (the API still checks access to it).
  const sourcePreview = config.sourceDatabase && (config.method || 'get').toLowerCase() === 'get'
    && urlDatabase().kind === 'free';
  if (sourcePreview) {
    config.headers['X-Database-Id'] = config.sourceDatabase;
  } else if (config.sourceDatabase) {
    throw blockedRequest(config, 'a source-database preview is a read from a slug-free page only');
  } else if (target.blocked) {
    if (!isDbFree) throw blockedRequest(config, target.blocked);
    delete config.headers['X-Database-Id'];
  } else if (explicit && !config._dbHeaderRetried && explicit !== target.databaseName) {
    throw blockedRequest(config, `it names ${explicit} but this tab is on ${target.databaseName || 'no database'}`);
  } else if (target.databaseName && !config._dbHeaderRetried) {
    config.headers['X-Database-Id'] = target.databaseName;
  }
  // The person's zone (Timezone preference, else the browser's). The API uses it wherever it
  // turns an instant into a day: a "stamp today" into a date column, day filters and per-day
  // grouping on UTC timestamps.
  config.headers['X-Time-Zone'] = effectiveTimeZone();
  return config;
});

// View-based lookups (Parent_Unique, Panel_Unique, Positive_Terminal, etc.) derive
// their values from live project-DB queries. When a row is created/updated/deleted
// via dynamicApi, the set of possible dropdown values may have changed. Dispatch a
// window event so LookupContext refetches — backend already purges its own cache
// on the same events (see DynamicController.InvalidateLookupsCache).
api.interceptors.response.use(response => {
  const method = response.config.method?.toLowerCase();
  const url = response.config.url || '';
  if ((method === 'post' || method === 'put' || method === 'delete') && url.includes('/api/dynamic/')) {
    window.dispatchEvent(new Event('pmd-lookups-invalidated'));
  }
  return response;
});

// The API rejects an X-Database-Id that is not an active database (stale localStorage after a
// database is renamed or decommissioned) with 400 code 'unknown-database', rather than quietly
// running against the template, and one the user can no longer reach (their role on it was
// removed) with 403 code 'database-access-denied'. Without recovery the second fails every call,
// /api/auth/me included, and the gate dead-ends on "Account not verified". Forget the selection
// either way. Other 403s are permission denials inside a reachable database and pass through. Admin-DB endpoints the gate
// calls before a database is resolved (auth, users, the database list) never needed the header,
// so they retry once without it. Anything else reloads the current URL: a /<slug>/ route
// re-resolves the database from the slug, and a slug-free route with no selection falls through
// to the picker. The session flag stops a second reload within a few seconds from looping.
let unknownDbNavigating = false;
api.interceptors.response.use(undefined, error => {
  const { response, config } = error;
  const staleDb = (response?.status === 400 && response.data?.code === 'unknown-database')
    || (response?.status === 403 && response.data?.code === 'database-access-denied');
  if (!staleDb || !config) {
    return Promise.reject(error);
  }
  clearDatabaseSelection();
  if (!config._dbHeaderRetried && DB_FREE_PREFIXES.some(p => (config.url || '').startsWith(p))) {
    config._dbHeaderRetried = true;
    delete config.headers['X-Database-Id'];
    return api(config);
  }
  // Several in-flight calls can fail together; only the first one navigates.
  if (unknownDbNavigating) return Promise.reject(error);
  unknownDbNavigating = true;
  let lastReload = 0;
  try { lastReload = Number(sessionStorage.getItem('pmd-unknown-db-reload')) || 0; } catch { /* storage blocked */ }
  if (Date.now() - lastReload > 10000) {
    try { sessionStorage.setItem('pmd-unknown-db-reload', String(Date.now())); } catch { /* storage blocked */ }
    window.location.replace(window.location.href);
  } else if (window.location.pathname !== '/select-database') {
    window.location.assign('/select-database');
  }
  return Promise.reject(error);
});

export const adminUsersApi = {
  getAll: () => api.get('/api/auth/users')
};

export const authApi = {
  getMe: () => api.get('/api/auth/me')
};

// ===== Development APIs =====
export const devFilesApi = {
  // People who can be picked as an owner on the DevTrack boards.
  getDevTrackRoster: () => api.get('/api/devtrack/roster'),
  // The wider nameable set — the roster plus anyone who can report a bug. Feeds the
  // Originator picker, so a reporter stays selectable on the row they raised.
  getDevTrackOriginators: () => api.get('/api/devtrack/roster?scope=originator'),
  // Footer bug reporter. A separate endpoint from createTask on purpose: that one is
  // SysAdmin-only, this one is open to action.bugreport and stamps everything that
  // decides what the row means server-side.
  reportBug: (report) => api.post('/api/devtrack/bugreport', report),
  // Task Log (SQL-backed via DevTrack)
  getTaskLogJson: () => api.get('/api/devtrack/tasks'),
  createTask: (task) => api.post('/api/devtrack/tasks', task),
  updateTask: (id, task) => api.put(`/api/devtrack/tasks/${id}`, task),
  deleteTask: (id) => api.delete(`/api/devtrack/tasks/${id}`),
  getTasksArchive: () => api.get('/api/devtrack/tasks/archive'),
  archiveCompletedTasks: () => api.post('/api/devtrack/tasks/archive'),
  // Dev Plan (SQL-backed via DevTrack)
  getDevPlan: () => api.get('/api/devtrack/plan'),
  updateDevPlanStep: (stepNumber, step) => api.put(`/api/devtrack/plan/step/${stepNumber}`, step),
  addDevPlanStep: (step) => api.post('/api/devtrack/plan', step),
  deleteDevPlanStep: (stepNumber) => api.delete(`/api/devtrack/plan/step/${stepNumber}`),
  moveDevPlanStep: (stepNumber, direction) => api.post(`/api/devtrack/plan/step/${stepNumber}/move`, { direction }),
  reassignDevPlanStep: (stepNumber, targetStep) => api.post(`/api/devtrack/plan/step/${stepNumber}/reassign`, { targetStep }),
  closeDevPlanStep: (stepNumber, summary, changes) => api.post(`/api/devtrack/plan/step/${stepNumber}/close`, { summary, changes }),
  reorderDevPlan: (items) => api.put('/api/devtrack/plan/reorder', items),
  // Rev Log (SQL-backed via DevTrack)
  getRevLog: () => api.get('/api/devtrack/revlog'),
  addRevision: (revision) => api.post('/api/devtrack/revlog', revision),
  updateRevision: (id, revision) => api.put(`/api/devtrack/revlog/${id}`, revision),
  deleteRevision: (id) => api.delete(`/api/devtrack/revlog/${id}`),
  // Lightweight version fetch — the only rev-log endpoint that's NOT
  // SysAdmin-gated (backed by [AllowAnonymous] on the controller). Used
  // by Footer + ReportViewer for the dynamic version string.
  getCurrentVersion: () => api.get('/api/devtrack/revlog/current-version'),
  // Schema & Dev Tools (still file-based)
  // Blank reads the request's database; a database name or '__admin__' reads that one instead.
  getLiveSchema: (database) => api.get('/api/devfiles/schema/live', database ? { params: { database } } : undefined),
  getClaudeNotes: () => api.get('/api/devfiles/claude-notes'),
  updateClaudeNotes: (content) => api.put('/api/devfiles/claude-notes', { content }),
  getClaudeNotesFiles: () => api.get('/api/devfiles/claude-notes/files'),
  getClaudeNotesFile: (name) => api.get(`/api/devfiles/claude-notes/files/${encodeURIComponent(name)}`),
  // Module Configs (SQL-backed via AppCfg_DynamicModules)
  getModuleConfigs: () => api.get('/api/devfiles/module-configs'),
  // Moved to /api/module-config (a20.046) — runtime UI metadata, not a
  // dev-only endpoint. Backed by ModuleConfigController with [RequireDbAccess].
  // DevFilesController still owns PUT/DELETE/refresh-schema under [RequireSysAdmin].
  getModuleConfig: (tableName) => api.get(`/api/module-config/${tableName}`),
  saveModuleConfig: (tableName, data) => api.put(`/api/devfiles/module-config/${tableName}`, data),
  refreshModuleConfigSchema: (tableName) => api.post(`/api/devfiles/module-config/${tableName}/refresh-schema`),
  deleteModuleConfig: (tableName) => api.delete(`/api/devfiles/module-config/${tableName}`),
  // API status
  getTablesWithApi: () => api.get('/api/devfiles/tables-with-api'),
  // Table field names (lightweight — for FK target dropdowns)
  getTableFields: (tableName, database) => api.get(`/api/devfiles/table-fields/${tableName}`, database ? { params: { database } } : undefined),
  // UI Audit — style guide computed styles + usage scanner
  getUiAuditStyles: (cssClass) => api.get(`/api/devfiles/ui-audit/styles`, { params: { class: cssClass } }),
  getUiAuditUsages: (cssClass) => api.get(`/api/devfiles/ui-audit/usages`, { params: { class: cssClass } })
};

// ===== Prod Deploy API =====
export const prodDeployApi = {
  getUndeployed:  () => api.get('/api/devtrack/prod-deploy/undeployed'),
  execute:        (dto) => api.post('/api/devtrack/prod-deploy/execute', dto),
  workflowStatus: () => api.get('/api/devtrack/prod-deploy/workflow-status'),
};

// ===== Task Transfer API (Prod task log -> Pre-Prod) =====
export const taskTransferApi = {
  getCandidates: () => api.get('/api/devtrack/task-transfer/candidates'),
  execute:       (ids) => api.post('/api/devtrack/task-transfer/execute', { ids }),
};

// ===== Dynamic API - Generic CRUD with auto FK resolution =====
export const dynamicApi = {
  getAll: (tableName, params = {}) => {
    const qs = new URLSearchParams();
    if (params.limit) qs.append('limit', params.limit);
    if (params.search) qs.append('search', params.search);
    if (params.orderBy) qs.append('orderBy', params.orderBy);
    if (params.orderDir) qs.append('orderDir', params.orderDir);
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return api.get(`/api/dynamic/${tableName}${query}`);
  },
  // A development tool's sample rows from the source database it is pointed at (see
  // SourceDatabasePicker), which need not be this tab's database. Read-only, slug-free pages only.
  // With no source yet (the database list has not loaded) there is nothing to preview: falling
  // back to the tab's database is exactly what this is here to avoid.
  previewFrom: (tableName, database, limit = 5) => (database
    ? api.get(`/api/dynamic/${tableName}?limit=${limit}`, { sourceDatabase: database })
    : Promise.resolve({ data: [] })),
  getFiltered: (tableName, filterField, filterValue) =>
    api.get(`/api/dynamic/${tableName}?filterField=${filterField}&filterValue=${filterValue}`),
  // Same as getFiltered, but the value is URL-encoded. Use it for free-text values - a
  // transfer number or name containing '&', '#' or '+' would otherwise truncate the query.
  getFilteredEncoded: (tableName, filterField, filterValue) =>
    api.get(`/api/dynamic/${encodeURIComponent(tableName)}?filterField=${encodeURIComponent(filterField)}&filterValue=${encodeURIComponent(filterValue ?? '')}`),
  getById: (tableName, id) => api.get(`/api/dynamic/${tableName}/${id}`),
  getLookups: (tableName, fieldName) => api.get(`/api/dynamic/${tableName}/lookups/${fieldName}`),
  create: (tableName, data, options = {}) => api.post(`/api/dynamic/${tableName}`, data, options),
  update: (tableName, id, data, options = {}) => api.put(`/api/dynamic/${tableName}/${id}`, data, options),
  delete: (tableName, id, options = {}) => api.delete(`/api/dynamic/${tableName}/${id}`, options),
  // Single-transaction bulk delete. Backend rolls back all deletes if any one
  // fails, so we never end up in a half-deleted state (was a real bug with the
  // previous Promise.all(deletes) implementation — deadlocked under contention
  // and left the user with partial state and no way to tell which failed).
  deleteMultiple: (tableName, ids, options = {}) =>
    api.post(`/api/dynamic/${tableName}/delete-bulk`, { ids }, options)
};

// ===== DS Project — this database's Index Sheet Datasheets and their values =====
// Per-database key utilities.ds.project.composer; the X-Database-Id interceptor scopes every call.
// Write calls take a trailing axios config so the caller can pass the X-Changed-By audit
// header — the DS tables stamp DS_RevUser/Rev_User from it when auth is disabled locally and
// there are no claims to read. Convention matches DynamicModule.js and importEngine.js.
export const dsProjectApi = {
  getSheets: () => api.get('/api/ds/project/sheets'),
  getIsaTemplates: () => api.get('/api/ds/project/isa-templates'),
  getDiff: (sheetId) => api.get(`/api/ds/project/sheets/${sheetId}/diff`),
  createDatasheet: (sheetId, templateId, cfg) => api.post(`/api/ds/project/sheets/${sheetId}/create`, { templateId }, cfg),
  assign: (sheetId, dsIndexId, cfg) => api.put(`/api/ds/project/sheets/${sheetId}/assign`, { dsIndexId }, cfg),
  revise: (sheetId, cfg) => api.post(`/api/ds/project/sheets/${sheetId}/revise`, {}, cfg),
  refresh: (sheetId, cfg) => api.post(`/api/ds/project/sheets/${sheetId}/refresh`, {}, cfg),
  getLayout: (dsIndexId) => api.get(`/api/ds/project/templates/${dsIndexId}/layout`),
  saveLayout: (dsIndexId, changes, cfg) => api.put(`/api/ds/project/templates/${dsIndexId}/layout`, changes, cfg),
  getTemplates: () => api.get('/api/ds/project/templates'),
  getLayoutSpec: () => api.get('/api/ds/project/layout-spec'),
  getComposerLines: (dsIndexId) => api.get(`/api/ds/project/composer/${dsIndexId}`),
  getValues: (indexSheetId) => api.get(`/api/ds/project/values?indexSheetId=${indexSheetId}`),
  createValue: (data, cfg) => api.post('/api/ds/project/values', data, cfg),
  updateValue: (id, data, cfg) => api.put(`/api/ds/project/values/${id}`, data, cfg)
};

// ===== DS Composer API =====
export const dsComposerApi = {
  getIndex: (type) => api.get(type ? `/api/ds/index?type=${encodeURIComponent(type)}` : '/api/ds/index'),
  getTemplates: () => api.get('/api/ds/templates'),
  createIndex: (data, cfg) => api.post('/api/ds/index', data, cfg),
  duplicateIndex: (sourceId, data, cfg) => api.post(`/api/ds/index/${sourceId}/duplicate`, data, cfg),
  setPublished: (id, published, cfg) => api.put(`/api/ds/index/${id}/publish`, { published }, cfg),
  getComposerLines: (dsIndexId) => api.get(`/api/ds/composer/${dsIndexId}`),
  getContentLibrary: () => api.get('/api/ds/library'),
  reorderLines: (dsIndexId, ids, cfg) => api.put(`/api/ds/composer/${dsIndexId}/reorder`, { ids }, cfg),
  createConfigLine: (data, cfg) => api.post('/api/ds/config/line', data, cfg),
  updateConfigLine: (id, data, cfg) => api.put(`/api/ds/config/line/${id}`, data, cfg),
  deleteConfigLine: (id, cfg) => api.delete(`/api/ds/config/line/${id}`, cfg)
};

// ===== DS Master Catalog — catalog curation, Compare/Review, promote, master values, Update Template DB =====
// Global key utilities.ds.master.catalog. Writes take the trailing axios config for X-Changed-By.
export const dsMasterCatalogApi = {
  getSheets: () => api.get('/api/ds/master-catalog/sheets'),
  getManufacturers: () => api.get('/api/ds/master-catalog/manufacturers'),
  getStatuses: () => api.get('/api/ds/master-catalog/statuses'),
  updateSheet: (id, body, cfg) => api.put(`/api/ds/master-catalog/sheets/${id}`, body, cfg),
  deleteSheet: (id, cfg) => api.delete(`/api/ds/master-catalog/sheets/${id}`, cfg),
  getValues: (sheetId, dsIndexId) =>
    api.get(`/api/ds/master-catalog/sheets/${sheetId}/values${dsIndexId ? `?dsIndexId=${dsIndexId}` : ''}`),
  createValue: (sheetId, body, cfg) => api.post(`/api/ds/master-catalog/sheets/${sheetId}/values`, body, cfg),
  updateValue: (valueId, body, cfg) => api.put(`/api/ds/master-catalog/values/${valueId}`, body, cfg),
  getDatabases: () => api.get('/api/ds/master-catalog/databases'),
  // No database compares every database the caller can open.
  compare: (database) =>
    api.get(`/api/ds/master-catalog/compare${database ? `?database=${encodeURIComponent(database)}` : ''}`),
  compareSheet: (database, indexSheetId) =>
    api.get(`/api/ds/master-catalog/compare/sheet?database=${encodeURIComponent(database)}&indexSheetId=${indexSheetId}`),
  promote: (body, cfg) => api.post('/api/ds/master-catalog/promote', body, cfg),
  promoteManufacturer: (body, cfg) => api.post('/api/ds/master-catalog/promote-manufacturer', body, cfg),
  previewTemplateDb: () => api.get('/api/ds/master-catalog/template-db'),
  updateTemplateDb: (cfg) => api.post('/api/ds/master-catalog/template-db', {}, cfg)
};

// ===== DS Project Catalog — browse the master catalog or another database, pull into this one =====
// Needs utilities.ds.project.composer AND utilities.ds.project.catalog on this database. `source` is
// 'catalog' or a project database name. Writes take the trailing axios config for X-Changed-By.
export const dsProjectCatalogApi = {
  getSources: () => api.get('/api/ds/project-catalog/sources'),
  browse: (source) => api.get(`/api/ds/project-catalog/browse?source=${encodeURIComponent(source)}`),
  sheetDiff: (source, sourceId) =>
    api.get(`/api/ds/project-catalog/sheet-diff?source=${encodeURIComponent(source)}&sourceId=${sourceId}`),
  // Per-sheet results: { results: [{ sourceId, indexSheet, status: pulled|skipped|failed, reason?, ... }] }.
  pull: (source, sheetIds, bringValues, cfg) =>
    api.post('/api/ds/project-catalog/pull', { source, sheetIds, bringValues }, cfg),
  // One master catalog sheet (UtlCfg_Index_Sheet.ID) into this database, values included by default.
  pullFromMaster: (catalogSheetId, cfg, bringValues = true) =>
    api.post('/api/ds/project-catalog/pull', { source: 'catalog', sheetIds: [catalogSheetId], bringValues }, cfg),
  updateFromSource: (source, sourceId, cfg) => api.post('/api/ds/project-catalog/update', { source, sourceId }, cfg),
  adoptRevision: (source, sourceId, cfg) => api.post('/api/ds/project-catalog/adopt', { source, sourceId }, cfg),
  newRevision: (source, sourceId, cfg) => api.post('/api/ds/project-catalog/new-revision', { source, sourceId }, cfg)
};

// ===== DS Definitions — datasheet label definitions (help keys dsdef:<label> / dsdef:<slug>:<label>) =====
// master: global utilities.ds.master.composer; project: per-database utilities.ds.project.composer
// (the X-Database-Id interceptor picks the database). Writes also need the global action.edithelp.
// Labels contain / & and :, so they travel in the query string or body, never the path.
// Empty contentHtml on save deletes, as in the help editor.
export const dsDefinitionsApi = {
  master: {
    getLabels: () => api.get('/api/ds/definitions/master/labels'),
    get: (label) => api.get('/api/ds/definitions/master/definition', { params: { label } }),
    // Templates carrying the label, with the sheets that use each.
    usage: (label) => api.get('/api/ds/definitions/master/usage', { params: { label } }),
    save: (label, contentHtml, cfg) => api.put('/api/ds/definitions/master/definition', { label, contentHtml }, cfg),
    remove: (label, cfg) => api.delete('/api/ds/definitions/master/definition', { ...cfg, params: { label } })
  },
  project: {
    getLabels: () => api.get('/api/ds/definitions/project/labels'),
    get: (label) => api.get('/api/ds/definitions/project/definition', { params: { label } }),
    // Templates carrying the label, with the sheets that use each.
    usage: (label) => api.get('/api/ds/definitions/project/usage', { params: { label } }),
    save: (label, contentHtml, cfg) => api.put('/api/ds/definitions/project/definition', { label, contentHtml }, cfg),
    remove: (label, cfg) => api.delete('/api/ds/definitions/project/definition', { ...cfg, params: { label } })
  }
};

// ===== DS Spec — Device Specification Utility: Index Sheet Builder, Device Spec, Panel Spec =====
// Per-database key utilities.ds.project.composer. propose is a read (POST only for the tag list).
// Writes take the trailing axios config for X-Changed-By.
export const dsSpecApi = {
  getIsaTypes: () => api.get('/api/ds/spec/isa-types'),
  getManufacturers: () => api.get('/api/ds/spec/manufacturers'),
  getSizes: () => api.get('/api/ds/spec/sizes'),
  getTags: (isaTypeId) => api.get('/api/ds/spec/tags', { params: { isaTypeId } }),
  nextInstance: (prefix, count = 1) => api.get('/api/ds/spec/next-instance', { params: { prefix, count } }),
  propose: (body) => api.post('/api/ds/spec/propose', body),
  // rows: [{ guided, code, manufacturerId, description, model, specification, type, status, tagIds, assignTags }]
  createIndexSheets: (rows, cfg) => api.post('/api/ds/spec/index-sheets', { rows }, cfg),
  // Index Sheets in the catalog and this database whose model / specification / description / code contains q.
  search: (q) => api.get('/api/ds/spec/search', { params: { q } }),
  getCatalog: (type, isa, showAll) => api.get('/api/ds/spec/catalog', { params: { type, isa: isa || undefined, showAll } }),
  getPanels: () => api.get('/api/ds/spec/panels'),
  setPanelSheet: (panelId, indexSheetId, expectedIndexSheetId, cfg) =>
    api.put(`/api/ds/spec/panels/${panelId}/index-sheet`, { indexSheetId, expectedIndexSheetId }, cfg)
};

// ===== ISA Datasheet Package — generation, review, publish (dev plan step 105) =====
// Project scoping is free: the X-Database-Id interceptor above puts the selected database
// on every request, and the server reads the project DB name off its own live connection
// rather than trusting the client.
//
// Write calls take a trailing axios config so the caller can pass the X-Changed-By audit
// header, same convention as dsProjectApi/dsComposerApi. It is not optional in practice: the
// server refuses a write it cannot attribute, because DS_PackageDrafter, DS_PackageApprover
// and Rev_User record who did what.
export const dsPackageApi = {
  list: () => api.get('/api/dspackage'),
  get: (id) => api.get(`/api/dspackage/${id}`),
  sources: () => api.get('/api/dspackage/sources'),
  // Read-only: safe to call as the user fiddles with the form, writes nothing.
  preflight: (body) => api.post('/api/dspackage/preflight', body),
  create: (body, cfg) => api.post('/api/dspackage', body, cfg),
  records: (id, groupBy = 'tag') => api.get(`/api/dspackage/${id}/records?groupBy=${groupBy}`),
  snapshot: (id, recordId) => api.get(`/api/dspackage/${id}/records/${recordId}/snapshot`),
  // Bulk and all-or-nothing. Approving an index group means posting every member id here,
  // never a loop of single updates — a half-approved group is unreadable after the fact.
  setRecordStatus: (id, recordIds, status, cfg) =>
    api.put(`/api/dspackage/${id}/records/status`, { recordIds, status }, cfg),
  setStatus: (id, status, cfg) => api.put(`/api/dspackage/${id}/status`, { status }, cfg),
  // Description and current revision remarks only; refused once the package is Approved.
  updateText: (id, body, cfg) => api.put(`/api/dspackage/${id}`, body, cfg),
  revise: (id, dsPackageRemarks, cfg) =>
    api.post(`/api/dspackage/${id}/revise`, { dsPackageRemarks }, cfg),
  // Megabytes of snapshots, hence POST. The server decides the watermark; the client renders
  // what it is told.
  exportPackage: (id, body, cfg) => api.post(`/api/dspackage/${id}/export`, body, cfg)
};

// ===== Transmittals — numbering and publish state =====
// Everything else on a transmittal goes through dynamicApi (Transmittal_Log, Transmittal_Item,
// Transmittal_Package). These three need the server: the number is a locked per-type MAX+1,
// and the publish stamp must name the caller. Trailing cfg carries X-Changed-By, as elsewhere.
export const transmittalApi = {
  assignNumber: (id, cfg) => api.post(`/api/transmittal/${id}/number`, null, cfg),
  publish: (id, cfg) => api.post(`/api/transmittal/${id}/publish`, null, cfg),
  unpublish: (id, cfg) => api.post(`/api/transmittal/${id}/unpublish`, null, cfg)
};

// ===== Admin Dynamic API — table/column discovery for Field Manager FK Config =====
export const adminDynamicApi = {
  getTables: () => api.get('/api/dynamic/admin-tables'),
  getColumns: (table) => api.get(`/api/dynamic/admin-tables/${encodeURIComponent(table)}/columns`)
};

// ===== Project Schema API — table/column discovery for DS Composer =====
export const projectSchemaApi = {
  getTables: () => api.get('/api/dynamic/project-tables'),
  getColumns: (table) => api.get(`/api/dynamic/project-tables/${encodeURIComponent(table)}/columns`)
};

// ===== KPI API - Dashboard aggregations =====
export const kpiApi = {
  getHome: () => api.get('/api/kpi/home'),
  getDocumentation: () => api.get('/api/kpi/documentation'),
  getNetworking: () => api.get('/api/kpi/networking'),
  getEquipment: () => api.get('/api/kpi/equipment'),
  getSoo: () => api.get('/api/kpi/soo')
};

// ===== App Configuration API =====
export const appCfgApi = {
  // Display Settings
  getDisplaySettings: () => api.get('/api/appcfg/display-settings'),
  getDisplaySetting: (key) => api.get(`/api/appcfg/display-settings/${key}`),
  updateDisplaySetting: (id, data) => api.put(`/api/appcfg/display-settings/${id}`, data),

  // Field Metadata
  getAllFieldMetadata: () => api.get('/api/appcfg/fieldmetadata'),
  getTableList: () => api.get('/api/appcfg/fieldmetadata/tables'),
  getFieldMetadata: (tableName) => api.get(`/api/appcfg/fieldmetadata/${tableName}`),
  updateFieldMetadata: (id, data) => api.put(`/api/appcfg/fieldmetadata/${id}`, data),
  updateFieldMetadataBatch: (fields) => api.put('/api/appcfg/fieldmetadata/batch', fields),

  // Sync
  syncFieldMetadata: () => api.post('/api/appcfg/sync'),

  // Database metadata
  getDatabaseViews: (database) => api.get('/api/appcfg/views', database ? { params: { database } } : undefined),
  getViewColumns: (viewName, database) => api.get(`/api/appcfg/views/${encodeURIComponent(viewName)}/columns`, database ? { params: { database } } : undefined),

  // Lookup Categories
  getAllLookupCategories: () => api.get('/api/appcfg/lookup-categories'),
  getLookupCategory: (category) => api.get(`/api/appcfg/lookup-categories/${category}`),
  createLookupCategory: (data) => api.post('/api/appcfg/lookup-categories', data),
  updateLookupCategory: (category, data) => api.put(`/api/appcfg/lookup-categories/${category}`, data),
  deleteLookupCategory: (category) => api.delete(`/api/appcfg/lookup-categories/${category}`),

  // Lookup Values
  getAllLookups: () => api.get('/api/appcfg/lookups'),
  getLookupsByCategory: (category) => api.get(`/api/appcfg/lookups/${category}`),
  getLookupCategories: () => api.get('/api/appcfg/lookups/categories'),
  createLookup: (data) => api.post('/api/appcfg/lookups', data),
  updateLookup: (id, data) => api.put(`/api/appcfg/lookups/${id}`, data),
  deleteLookup: (id) => api.delete(`/api/appcfg/lookups/${id}`)
};

// ===== Database Sync API =====
export const syncApi = {
  start: (request) => api.post('/api/sync/start', request),
  getStatus: () => api.get('/api/sync/status'),
  getHistory: (project) => api.get(`/api/sync/history/${project}`),
  getAllHistory: () => api.get('/api/sync/history'),
  getAvailability: () => api.get('/api/sync/availability'),
  getPlan: (project, phase) => api.get(`/api/sync/plan/${project}/${phase}`),
  syncViews: (target, environment) => api.post('/api/sync/sync-views', { target, environment }),
  getViewTargets: (environment) => api.get('/api/sync/view-targets', { params: { environment } }),
  getViewHistory: (target) => api.get(`/api/sync/view-history/${target}`)
};

// ===== Help API =====
// Per-page help content (AppCfg_HelpContent, admin DB). Reads are open to any
// authenticated user; save requires the action.edithelp permission.
export const helpApi = {
  list: () => api.get('/api/help'),
  get: (key) => api.get(`/api/help/${encodeURIComponent(key)}`),
  save: (key, title, contentHtml) => api.put(`/api/help/${encodeURIComponent(key)}`, { title, contentHtml })
};

// ===== Email API =====
// Portable email popup (StuiEmailModal). The caller names an audience; the server resolves the
// recipients, adds the subject prefix, sanitizes the rich-text body and sends from the no-reply
// mailbox with Reply-To set to the signed-in user. Preview builds the same email without sending.
// Payload: { audience, subjectQualifier, subject, bodyHtml, context, recipientIds, template, query }
export const emailApi = {
  send: (payload) => api.post('/api/email/send', payload),
  preview: (payload) => api.post('/api/email/preview', payload),
};

// ===== Local Dev Config API =====
// Dev-VM-only window onto appsettings.Development.json (Preferences > Local Dev tab).
// Backend 404s outside local dev; writes are limited to the DataSync subtree.
export const localDevApi = {
  getConfig: () => api.get('/api/localdev/config'),
  writeConfig: (op, value) => api.put('/api/localdev/config', { op, value })
};

// ===== Schema Sync API =====
export const schemaSyncApi = {
  start: (request) => api.post('/api/schema-sync/start', request),
  getStatus: () => api.get('/api/schema-sync/status'),
  getHistory: (project) => api.get(`/api/schema-sync/history/${project}`)
};

// ===== Admin Config Sync API (DevPlan step 86) =====
// Reconciles AppCfg_* config between the preprod and prod ADMIN databases.
// getDiff is read-only. apply requires an explicit row list AND the target database name
// typed back — there is deliberately no "sync everything" call.
export const adminConfigSyncApi = {
  getAvailability: () => api.get('/api/adminconfigsync/availability'),
  getDirections: () => api.get('/api/adminconfigsync/directions'),
  getDiff: (direction) => api.get('/api/adminconfigsync/diff', { params: { direction } }),
  apply: (direction, rows, confirmTargetDatabase) =>
    api.post('/api/adminconfigsync/apply', { direction, rows, confirmTargetDatabase }),
  getHistory: () => api.get('/api/adminconfigsync/history')
};

// ===== Admin Management API (Step 37 - RBAC) =====
export const adminManagementApi = {
  // Summary
  getSummary: () => api.get('/api/admin/summary'),
  // Table Ownership — governs DynamicController CRUD authorization (pen test R2)
  getTableOwners:   () => api.get('/api/admin/table-owner'),
  getTableOwner:    (tableName) => api.get(`/api/admin/table-owner/${tableName}`),
  createTableOwner: (data) => api.post('/api/admin/table-owner', data),
  updateTableOwner: (tableName, data) => api.put(`/api/admin/table-owner/${tableName}`, data),
  deleteTableOwner: (tableName) => api.delete(`/api/admin/table-owner/${tableName}`),
  // Organizations
  getOrganizations: () => api.get('/api/admin/organizations'),
  getOrganization: (id) => api.get(`/api/admin/organizations/${id}`),
  createOrganization: (data) => api.post('/api/admin/organizations', data),
  updateOrganization: (id, data) => api.put(`/api/admin/organizations/${id}`, data),
  deleteOrganization: (id) => api.delete(`/api/admin/organizations/${id}`),
  // Databases
  getDatabases: () => api.get('/api/admin/databases'),
  getDatabase: (id) => api.get(`/api/admin/databases/${id}`),
  createDatabase: (data) => api.post('/api/admin/databases', data),
  updateDatabase: (id, data) => api.put(`/api/admin/databases/${id}`, data),
  // Database provisioning (step 66)
  provisionDatabase: (data) => api.post('/api/admin/databases/provision', data),
  decommissionDatabase: (id) => api.post(`/api/admin/databases/${id}/decommission`),
  restoreDatabase: (id) => api.post(`/api/admin/databases/${id}/restore`),
  hardDeleteDatabase: (id, data) => api.delete(`/api/admin/databases/${id}`, { data }),
  getProvisioningRecord: (recordId) => api.get(`/api/admin/databases/provisioning/${recordId}`),
  getProvisioningHistory: (limit = 20) => api.get(`/api/admin/databases/provisioning?limit=${limit}`),
  // Permissions
  getPermissions: () => api.get('/api/admin/permissions'),
  getPermission: (id) => api.get(`/api/admin/permissions/${id}`),
  createPermission: (data) => api.post('/api/admin/permissions', data),
  updatePermission: (id, data) => api.put(`/api/admin/permissions/${id}`, data),
  // Roles (DefRoles capability matrix)
  getDefRoles: () => api.get('/api/admin/roles'),
  getRoleMatrix: () => api.get('/api/admin/roles/matrix'),
  createDefRole: (data) => api.post('/api/admin/roles', data),
  updateDefRole: (id, data) => api.put(`/api/admin/roles/${id}`, data),
  updateRoleMatrix: (updates) => api.put('/api/admin/roles/matrix', updates),
  deleteDefRole: (id) => api.delete(`/api/admin/roles/${id}`),
  // User Roles
  getUserRoles: () => api.get('/api/admin/userroles'),
  getUserRolesForUser: (userId) => api.get(`/api/admin/userroles/user/${userId}`),
  createUserRole: (data) => api.post('/api/admin/userroles', data),
  updateUserRole: (id, data) => api.put(`/api/admin/userroles/${id}`, data),
  deleteUserRole: (id) => api.delete(`/api/admin/userroles/${id}`),
  // User Permissions (overrides)
  getUserPermissions: () => api.get('/api/admin/userpermissions'),
  getPermissionsForUserRole: (userRoleId) => api.get(`/api/admin/userpermissions/userrole/${userRoleId}`),
  createUserPermission: (data) => api.post('/api/admin/userpermissions', data),
  updateUserPermission: (id, data) => api.put(`/api/admin/userpermissions/${id}`, data),
  deleteUserPermission: (id) => api.delete(`/api/admin/userpermissions/${id}`),
  // Global Permissions
  getGlobalPermissions: (userId) => api.get(`/api/admin/users/${userId}/global-permissions`),
  addGlobalPermission: (userId, data) => api.post(`/api/admin/users/${userId}/global-permissions`, data),
  updateGlobalPermission: (id, data) => api.put(`/api/admin/global-permissions/${id}`, data),
  deleteGlobalPermission: (id) => api.delete(`/api/admin/global-permissions/${id}`),
  // One transactional Save for Admin > Users: user fields, roles, overrides and globals together.
  saveUserSecurity: (userId, batch) => api.put(`/api/admin/users/${userId}/security`, batch),
  // Users (admin management view)
  getUsers: () => api.get('/api/admin/users'),
  getUser: (id) => api.get(`/api/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/api/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/api/admin/users/${id}`),
  copyUserRoles: (sourceId, targetId) => api.post(`/api/admin/users/${sourceId}/copy-to/${targetId}`),
  resetUserPermissions: (userId) => api.post(`/api/admin/users/${userId}/reset-permissions`),
  // System Health
  getSystemHealth: (period = '1h') => api.get('/api/admin/system/health', { params: { period } }),
  getDatabaseSizes: () => api.get('/api/admin/system/database-sizes'),
  // Permission Resolution
  resolvePermissions: (userId, dbId) => api.get(`/api/admin/resolve/${userId}/${dbId}`),
  resolveAllPermissions: (userId) => api.get(`/api/auth/resolve-all/${userId}`),
  // Export
  exportUsers: () => api.get('/api/admin/export/users', { responseType: 'blob' }),
  exportRoleMatrix: () => api.get('/api/admin/export/role-matrix', { responseType: 'blob' }),
  // Import
  importUserRoles: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/admin/import/userroles', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Organization Images
  uploadOrgImage: (orgId, imageType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/admin/organizations/${orgId}/upload/${imageType}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteOrgImage: (orgId, imageType) => api.delete(`/api/admin/organizations/${orgId}/image/${imageType}`),
  // User creation & invitations
  createUser:        (data)   => api.post('/api/admin/users', data),
  getInvitation:     (userId) => api.get(`/api/admin/users/${userId}/invitation`),
  sendInvite:        (userId) => api.post(`/api/admin/users/${userId}/invite`),
  revokeInvite:      (userId) => api.delete(`/api/admin/users/${userId}/invite`),
  sendTestEmail:     (userId) => api.post(`/api/admin/users/${userId}/test-email`),
};

// ===== Invitations API (public — no auth) =====
export const invitationApi = {
  validate: (token)              => api.get(`/api/invitations/${token}`),
  accept:   (token, accessToken) => axios.post(
    `${API_BASE_URL}/api/invitations/${token}/accept`,
    {},
    { headers: { Authorization: `Bearer ${accessToken}` } }
  ),
};

// ===== Data Entry Module API =====
export const dataEntryApi = {
  getModules:     ()               => api.get('/api/de-modules'),
  getModule:      (slug)           => api.get(`/api/de-modules/${slug}`),
  saveModule:     (slug, data)     => api.put(`/api/de-modules/${slug}`, data),
  deleteModule:   (slug)           => api.delete(`/api/de-modules/${slug}`),
  getTableSchema: (tableName, database) => api.get(`/api/de-modules/schema/${tableName}`, database ? { params: { database } } : undefined),
  getProjectTables: (database)          => api.get('/api/de-modules/tables', database ? { params: { database } } : undefined)
};

// ===== Report Subscriptions API (DevPlan step 103) =====
// Scoped to the caller server-side; a SysAdmin sees all. You subscribe YOURSELF — there is no
// recipient parameter, because a subscribe-others capability would mail a report rendered under
// one person's permissions to someone who may have none.
export const reportSubscriptionsApi = {
  list:    (params)  => api.get('/api/reportsubscriptions', { params }),
  create:  (dto)     => api.post('/api/reportsubscriptions', dto),
  update:  (id, dto) => api.put(`/api/reportsubscriptions/${id}`, dto),
  remove:  (id)      => api.delete(`/api/reportsubscriptions/${id}`),
  sendNow: (id)      => api.post(`/api/reportsubscriptions/${id}/send-now`),
};

// ===== Database Selector API =====
export const databasesApi = {
  // Every load of the list refreshes the slug map, which routes each tab's requests (activeDatabase.js).
  getAvailable: () => api.get('/api/databases/available').then(res => { rememberDatabaseList(res.data); return res; }),
  // The database's organization logo, through the API so it can be used as a CSS mask
  getLogo: (slug) => api.get(`/api/databases/by-name/${encodeURIComponent(slug)}/logo`, { responseType: 'blob' }),
};

// ===== User Preferences API =====
// userId: optional — self by default; SysAdmin may target another user
// (impersonation writes pass the target id explicitly; see PreferencesContext).
export const preferencesApi = {
  get:     (userId)            => api.get('/api/preferences', { params: userId != null ? { userId } : {} }),
  save:    (rows, userId)      => api.put('/api/preferences', rows, { params: userId != null ? { userId } : {} }),
  restore: (userId, dbId)      => api.delete('/api/preferences', {
    params: {
      ...(userId != null ? { userId } : {}),
      ...(dbId != null ? { databaseInstanceId: dbId } : {}),
    },
  }),
  // SysAdmin only — AppSec_PreferenceDefaults tier (Preference Defaults page)
  getDefaults:  ()     => api.get('/api/preferences/defaults'),
  saveDefaults: (rows) => api.put('/api/preferences/defaults', rows),
};

// ===== Themes API =====
// userId: optional — only needed when the API has no signed-in user (local dev, impersonation),
// the same convention as preferencesApi.
export const themesApi = {
  list:    (userId)                    => api.get('/api/themes', { params: userId != null ? { userId } : {} }),
  usage:   (key, userId)               => api.get(`/api/themes/${encodeURIComponent(key)}/usage`, { params: userId != null ? { userId } : {} }),
  create:  (theme, userId)             => api.post('/api/themes', theme, { params: userId != null ? { userId } : {} }),
  update:  (key, patch, userId)        => api.put(`/api/themes/${encodeURIComponent(key)}`, patch, { params: userId != null ? { userId } : {} }),
  remove:  (key, replaceWith, userId)  => api.delete(`/api/themes/${encodeURIComponent(key)}`, {
    params: { ...(replaceWith ? { replaceWith } : {}), ...(userId != null ? { userId } : {}) },
  }),
  applyTo: (key, target, userId)       => api.post(`/api/themes/${encodeURIComponent(key)}/apply-to/${encodeURIComponent(target)}`, null, { params: userId != null ? { userId } : {} }),
};

// ===== Reporting API =====
// ===== Navigation =====
// The sidebar tree (AppCfg_Navigation), filtered on the server for the person asking, and what
// that person may do on a page's tables. asUserId (SysAdmin only) answers for someone else, which
// is how impersonation previews another person's sidebar and buttons.
export const navigationApi = {
  tree: (asUserId) => api.get('/api/navigation/tree', { params: asUserId ? { asUserId } : {} }),
  capabilities: (navKey, asUserId) =>
    api.get(`/api/navigation/${encodeURIComponent(navKey)}/capabilities`, { params: asUserId ? { asUserId } : {} }),
};

// Navigation editor (SysAdmin). A save that reaches past the row comes back 409 with `consequences`;
// send it again with confirm: true.
export const navigationAdminApi = {
  rows: () => api.get('/api/admin/navigation'),
  targets: () => api.get('/api/admin/navigation/targets'),
  create: (row) => api.post('/api/admin/navigation', row),
  update: (navKey, row) => api.put(`/api/admin/navigation/${encodeURIComponent(navKey)}`, row),
};

// Permission map (SysAdmin): what every key opens, and one person's access to one page, gate by gate.
export const permissionMapApi = {
  map: () => api.get('/api/admin/permission-map'),
  matrix: (userId, databaseId) => api.get('/api/admin/permission-map/matrix', { params: { userId, databaseId } }),
};

export const reportingApi = {
  // Reports the caller may open, as { slug, name, description, section }; the server filters them.
  listConfigs: () => api.get('/api/report/configs'),
  getConfig: (reportId) => api.get(`/api/report/${reportId}/config`),
  runReport: (reportId, params) => api.post(`/api/report/${reportId}/run`, params),
  getParameterOptions: (reportId, parameterField, filters) => {
    const params = {};
    if (filters) params.filters = JSON.stringify(filters);
    return api.get(`/api/report/${reportId}/parameter-options/${parameterField}`, { params });
  }
};

// ===== Report Deliveries =====
// Files a report subscription delivered as a link (every subscribed PDF). The API re-checks the
// caller on both calls; download returns the file as a Blob.
export const reportDeliveriesApi = {
  get: (key) => api.get(`/api/report-deliveries/${encodeURIComponent(key)}`),
  download: (key) => api.get(`/api/report-deliveries/${encodeURIComponent(key)}/file`, { responseType: 'blob' }),
};

// ===== Report PDF jobs =====
// Server-built PDFs for reports over the browser's page ceiling: queue, poll, then download the
// finished file through reportDeliveriesApi with the deliveryKey a ready job returns.
export const reportPdfJobsApi = {
  create: (body) => api.post('/api/report-pdf-jobs', body),
  get: (id) => api.get(`/api/report-pdf-jobs/${id}`),
  mine: () => api.get('/api/report-pdf-jobs/mine'),
};

// ===== Image Proxy =====
// A remote image (org logos on blob storage) as a data URL for PDF rendering, or null when it
// cannot be had. Fetched through /api/proxy/image to dodge CORS on the blob origin, and through
// THIS client so the Entra token rides along: a bare fetch() carries no token, the proxy answers
// 401 wherever auth is on, and the error body then reaches the PDF as a broken image.
export const fetchImageAsDataUrl = async (url) => {
  if (!url) return null;
  try {
    const res = await api.get('/api/proxy/image', { params: { url }, responseType: 'blob' });
    const blob = res.data;
    if (!blob || blob.size === 0) return null;
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

// ===== Combined Tools API =====
export const combinedToolsApi = {
  getParents:   (slug)           => api.get(`/api/combined-tools/${slug}/parents`),
  getTags:      (slug, parent)   => api.get(`/api/combined-tools/${slug}/tags`, { params: { parent } }),
  getPanels:    (slug, parent)   => api.get(`/api/combined-tools/${slug}/panels`, { params: { parent } }),
  getPlatforms: (slug)           => api.get(`/api/combined-tools/${slug}/platforms`),
  generate:     (slug, body)     => api.post(`/api/combined-tools/${slug}/generate`, body),
};

// ===== User Activity API (cross-session transfer tracking) =====
export const userActivityApi = {
  getActive:  (userId)              => api.get('/api/useractivity',          { params: { userId } }),
  register:   (dto)                 => api.post('/api/useractivity',          dto),
  update:     (activityId, userId, dto) => api.patch(`/api/useractivity/${activityId}`, dto, { params: { userId } }),
};

// Data Transfer History reads (DataTransferController). scope: mine | database | all.
export const dataTransferApi = {
  getScopes:    ()       => api.get('/api/datatransfer/scopes'),
  getTransfers: (params) => api.get('/api/datatransfer', { params }),
  getRows:      (params) => api.get('/api/datatransfer/rows', { params }),
  getSummary:   (params) => api.get('/api/datatransfer/summary', { params }),
  getDistinct:  (params) => api.get('/api/datatransfer/distinct', { params }),
};

export const integrationConfigApi = {
  // Slugs
  getSlugs: () => api.get('/api/integration-config/slugs'),

  // Platforms
  getPlatforms:    (slug)          => api.get(`/api/integration-config/${slug}/platforms`),
  createPlatform:  (slug, body)    => api.post(`/api/integration-config/${slug}/platforms`, body),
  updatePlatform:  (slug, id, body)=> api.put(`/api/integration-config/${slug}/platforms/${id}`, body),
  deletePlatform:  (slug, id)      => api.delete(`/api/integration-config/${slug}/platforms/${id}`),

  // Register Templates
  getTemplates:   (slug, platformId)          => api.get(`/api/integration-config/${slug}/platforms/${platformId}/templates`),
  addTemplate:    (slug, platformId, body)    => api.post(`/api/integration-config/${slug}/platforms/${platformId}/templates`, body),
  updateTemplate: (slug, platformId, id, body)=> api.put(`/api/integration-config/${slug}/platforms/${platformId}/templates/${id}`, body),
  deleteTemplate: (slug, platformId, id)      => api.delete(`/api/integration-config/${slug}/platforms/${platformId}/templates/${id}`),

  // Tag XRef
  getTagXRef:    (slug)          => api.get(`/api/integration-config/${slug}/tagxref`),
  addTagXRef:    (slug, body)    => api.post(`/api/integration-config/${slug}/tagxref`, body),
  updateTagXRef: (slug, id, body)=> api.put(`/api/integration-config/${slug}/tagxref/${id}`, body),
  deleteTagXRef: (slug, id)      => api.delete(`/api/integration-config/${slug}/tagxref/${id}`),

  // Copy-from
  getPlatformsFrom: (slug, sourceSlug)      => api.get(`/api/integration-config/${slug}/platforms/from/${sourceSlug}`),
  copyPlatforms:    (slug, body)            => api.post(`/api/integration-config/${slug}/platforms/copy`, body),
  getTagXRefFrom:   (slug, sourceSlug)      => api.get(`/api/integration-config/${slug}/tagxref/from/${sourceSlug}`),
  copyTagXRef:      (slug, body)            => api.post(`/api/integration-config/${slug}/tagxref/copy`, body),
};

export const udtCommentApi = {
  getEngUnits: (slug)           => api.get(`/api/udt-comment/${slug}/eng-units`),
  analyze:     (slug, csvContent) => api.post(`/api/udt-comment/${slug}/analyze`, csvContent, { headers: { 'Content-Type': 'application/json' } }),
  generate:    (slug, body)       => api.post(`/api/udt-comment/${slug}/generate`, body),
};

export const scadaTagApi = {
  getVersions: (system)      => api.get(`/api/scada-tag/versions?system=${system}`),
  generate:    (slug, formData) => api.post(`/api/scada-tag/${slug}/generate`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const simulationTagApi = {
  getVersions:   (system)        => api.get(`/api/simulation-tag/versions?system=${system}`),
  getParents:    (slug)          => api.get(`/api/combined-tools/${slug}/parents`),
  getCandidates: (formData)      => api.post(`/api/simulation-tag/candidates`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  generate:      (slug, formData) => api.post(`/api/simulation-tag/${slug}/generate`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const auditApi = {
  getRecordHistory: (tableName, recordId) =>
    api.get(`/api/audit/changes/${tableName}/${recordId}`),
  getAllChanges: (params) =>
    api.get('/api/audit/changes', { params }),
  // Filter dropdown values for one column across the whole filtered set ({ values, truncated })
  getChangeDistinct: (params) =>
    api.get('/api/audit/changes/distinct', { params }),
  // Every matching row in the current sort, capped ({ rows, totalCount, truncated })
  exportChanges: (params) =>
    api.get('/api/audit/changes/export', { params }),
  purge: (olderThanDays) =>
    api.delete('/api/audit/purge', { params: { olderThanDays } }),
  // Dry run — what a purge WOULD delete, broken down by Bulk Import vs human edits.
  // Audit rows have no undo, so the count is shown before anything is destroyed.
  purgePreview: (olderThanDays) =>
    api.get('/api/audit/purge/preview', { params: { olderThanDays } }),
};

// Admin-database audit trail (DevTrack 127) — SysAdmin only. Distinct from
// auditApi above, which covers PROJECT database CRUD. The primary unit here is a
// BATCH (one HTTP request = one user action), not a row: a single click can emit
// dozens of rows, so the page lists batches and expands to rows on demand.
// No retention/purge endpoint by design — see DevTrack 125 (DB maintenance).
export const adminAuditApi = {
  getBatches: (params) =>
    api.get('/api/adminaudit/batches', { params }),
  getBatch: (batchId) =>
    api.get(`/api/adminaudit/batches/${batchId}`),
  getRecordHistory: (tableName, recordId) =>
    api.get(`/api/adminaudit/changes/${tableName}/${recordId}`),
  // Flat field rows for the whole filtered set — the export needs the detail the
  // collapsed batch view hides, not just whatever the user expanded.
  getRows: (params) =>
    api.get('/api/adminaudit/rows', { params }),
  // Retention (DevTrack 135). The policy is READ-ONLY here — it is edited on
  // Administration > Security > Preference Defaults (System tab).
  getRetention: (databaseInstanceId) =>
    api.get('/api/adminaudit/retention', { params: { databaseInstanceId } }),
  // Dry run for THIS trail, per category. overrideDays applies one number to every
  // category for a single run and is never saved to the policy; 0 means never.
  purgeTrailPreview: (overrideDays) =>
    api.get('/api/adminaudit/purge/trail', { params: { overrideDays } }),
  // Dry run of the FULL sweep the AUDIT_PURGE job performs — every history table across
  // every project DB plus the admin DB. Shown on the Scheduled Jobs console, because that
  // is where someone decides whether to enable the job.
  purgeSweepPreview: () =>
    api.get('/api/adminaudit/purge/preview'),
  purgeTrail: (overrideDays) =>
    api.delete('/api/adminaudit/purge/trail', { params: { overrideDays } }),
};

// Scheduled BACPAC backups + blob browser (DevTrack 125). SysAdmin-only endpoints.
// No download endpoint by design — a bacpac is the whole database in one file, so
// recovery happens through the Azure Portal / Storage Explorer instead.
export const dbBackupApi = {
  getJobs:    () => api.get('/api/dbbackup/jobs'),
  createJob:  (data) => api.post('/api/dbbackup/jobs', data),
  updateJob:  (id, data) => api.put(`/api/dbbackup/jobs/${id}`, data),
  deleteJob:  (id) => api.delete(`/api/dbbackup/jobs/${id}`),
  runNow:     (id) => api.post(`/api/dbbackup/jobs/${id}/run`),
  getStatus:  () => api.get('/api/dbbackup/status'),
  getRuns:    (params) => api.get('/api/dbbackup/runs', { params }),
  getBlobs:   (params) => api.get('/api/dbbackup/blobs', { params }),
  deleteBlob: (path) => api.delete('/api/dbbackup/blobs', { params: { path } }),
  reconcile:  () => api.post('/api/dbbackup/reconcile'),
};

// Cross-type scheduled jobs console (DevTrack 136). Read + trigger only — editing a
// schedule stays on the page that understands that job type (Database Backups for BACPAC).
export const scheduledJobsApi = {
  getJobs:   () => api.get('/api/scheduledjobs'),
  getRuns:   (params) => api.get('/api/scheduledjobs/runs', { params }),
  getStatus: () => api.get('/api/scheduledjobs/status'),
  runNow:    (id) => api.post(`/api/scheduledjobs/${id}/run`),
  // Generic schedule editing — enable/disable and timing only. Type-specific config
  // (bacpac retention, audit trail purge) stays on the page that owns it.
  updateSchedule: (id, data) => api.patch(`/api/scheduledjobs/${id}/schedule`, data),
  reconcile: () => api.post('/api/scheduledjobs/reconcile'),
};

// Credential inventory — Administration > System > Credential Health (task 89).
// Metadata only: this API has no concept of a credential's value.
export const credentialHealthApi = {
  getAll:  (includeInactive = false) => api.get('/api/admin/credential-health', { params: { includeInactive } }),
  getOne:  (id)   => api.get(`/api/admin/credential-health/${id}`),
  summary: ()     => api.get('/api/admin/credential-health/summary'),
  create:  (data) => api.post('/api/admin/credential-health', data),
  update:  (id, data) => api.put(`/api/admin/credential-health/${id}`, data),
  remove:  (id)   => api.delete(`/api/admin/credential-health/${id}`),
};

export const utilitiesApi = {
  getIoCounts: (spareThreshold = 0.20) =>
    api.get('/api/utilities/io-counts', { params: { spareThreshold } }),
  getDistanceApprox: (target) =>
    api.get('/api/utilities/distance-approx', { params: { target } }),
  getEstimateExportFilters: () =>
    api.get('/api/utilities/estimate-export/filters'),
  generateEstimateExport: (body) =>
    api.post('/api/utilities/estimate-export/rows', body),
};

export default api;
